import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Database, Users, Server, CheckCircle } from 'lucide-react';

const ConfidentialityPage: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 mix-blend-multiply" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Confidentiality & Data Security
          </h1>
          <p className="mt-6 max-w-3xl text-xl text-cyan-100">
            Your Data, Our Priority
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-600">
              At Prestige AI, we understand that confidentiality and data security are of paramount importance when handling financial statements, audit working papers, and other sensitive client data. Our AI-driven solutions are designed to enhance accuracy and efficiency while ensuring the highest levels of data protection.
            </p>
            <p className="text-xl text-gray-600">
              We leverage enterprise-grade security frameworks, providing robust safeguards that ensure your data remains private, secure, and compliant with regulatory and professional standards.
            </p>
          </div>

          {/* Enterprise-Grade Security Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center">
                  <Shield className="h-8 w-8 text-cyan-600" />
                  <h2 className="ml-4 text-2xl font-bold text-gray-900">1. Enterprise-Grade Security and Encryption</h2>
                </div>
                <p className="mt-4 text-gray-600">
                  We implement industry-leading security measures to safeguard all financial data and audit-related documents, including:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Encryption at Rest and in Transit</h3>
                      <p className="mt-2 text-gray-600">All documents and data are encrypted using AES-256 encryption while stored and TLS 1.2+ encryption during transmission, ensuring data protection at every stage.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Access Controls</h3>
                      <p className="mt-2 text-gray-600">Role-based access controls (RBAC) ensure that only authorized users within your firm can access AI-generated reports and processed files.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Secure Cloud Infrastructure</h3>
                      <p className="mt-2 text-gray-600">All data is processed within a highly secure, enterprise-grade environment, adhering to industry best practices for confidentiality and integrity.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Data Isolation</h3>
                      <p className="mt-2 text-gray-600">Each organization's data is handled separately, ensuring no unauthorized cross-access between firms or users.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confidentiality Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center">
                  <Lock className="h-8 w-8 text-cyan-600" />
                  <h2 className="ml-4 text-2xl font-bold text-gray-900">2. Confidentiality and Data Protection</h2>
                </div>
                <p className="mt-4 text-gray-600">
                  We recognize the sensitivity of audit and financial data. To uphold strict confidentiality, we enforce the following safeguards:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-red-100 text-red-600">
                      <span className="text-2xl font-bold">🚫</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">No Data Used for AI Model Training</h3>
                      <p className="mt-2 text-gray-600">Client data is never used to train AI models. It remains completely private and is only processed for the specific tasks requested by users.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-blue-100 text-blue-600">
                      <span className="text-2xl font-bold">📁</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">No Prestige AI Data Sharing with Third Parties</h3>
                      <p className="mt-2 text-gray-600">Prestige AI does not sell, share, or expose your firm's data to any external parties.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-green-100 text-green-600">
                      <span className="text-2xl font-bold">🔒</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Strict Access Controls</h3>
                      <p className="mt-2 text-gray-600">Only users within your firm can access AI-generated outputs, ensuring confidentiality at all levels.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-purple-100 text-purple-600">
                      <span className="text-2xl font-bold">🛡️</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Confidentiality Agreements</h3>
                      <p className="mt-2 text-gray-600">All personnel involved in system maintenance and support are bound by strict confidentiality agreements, reinforcing our commitment to data security.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Retention Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center">
                  <Database className="h-8 w-8 text-cyan-600" />
                  <h2 className="ml-4 text-2xl font-bold text-gray-900">3. Data Retention Policies: Transparency and Compliance</h2>
                </div>
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-900">How Long Is Your Data Retained?</h3>
                  <p className="mt-4 text-gray-600">
                    Prestige AI follows industry-standard data management practices, ensuring that data is retained only as long as necessary to fulfill its intended purpose or as required by applicable regulations. The retention period is 90 days unless applicable regulations require a longer retention period.
                  </p>
                  <div className="mt-6 space-y-6">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">Data Retention Policies</h4>
                      <p className="mt-2 text-gray-600">
                        Our partners apply security protocols that include data encryption, controlled access, and confidentiality measures to protect information. While data is stored securely, retention periods are not explicitly defined and may vary.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">User Ownership of AI-Generated Outputs</h4>
                      <p className="mt-2 text-gray-600">
                        Clients remain responsible for retaining AI-generated reports within their own internal record-keeping systems to ensure compliance with professional and regulatory standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Regulatory Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-cyan-600" />
                  <h2 className="ml-4 text-2xl font-bold text-gray-900">4. Regulatory Considerations</h2>
                </div>
                <div className="mt-6 prose prose-lg">
                  <p>
                    Prestige AI is committed to industry best practices for data protection and security. While our AI solutions incorporate robust confidentiality measures, encryption protocols, and access controls, it remains the responsibility of each firm to assess how AI-generated outputs align with their internal compliance policies and regulatory obligations.
                  </p>
                  <p>
                    Users should consider their country's data protection laws, professional guidelines, and firm-specific requirements when integrating AI into audit and financial workflows.
                  </p>
                  <p>
                    For firms operating under GDPR or similar regulations, Prestige AI follows industry standards for data security, privacy protection, and user control over data. If specific compliance requirements apply, users should consult their internal compliance teams to ensure proper handling of AI-generated content.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Secure Processing Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <div className="flex items-center">
                  <Server className="h-8 w-8 text-cyan-600" />
                  <h2 className="ml-4 text-2xl font-bold text-gray-900">5. Secure Processing and Risk Mitigation</h2>
                </div>
                <p className="mt-4 text-gray-600">
                  To ensure end-to-end security and auditability, Prestige AI employs:
                </p>
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-cyan-100 text-cyan-600">
                      <span className="text-2xl font-bold">🛡️</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Firewall and Intrusion Prevention</h3>
                      <p className="mt-2 text-gray-600">Real-time monitoring and defense mechanisms prevent unauthorized system access.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-cyan-100 text-cyan-600">
                      <span className="text-2xl font-bold">🛡️</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Independent Security Audits</h3>
                      <p className="mt-2 text-gray-600">The infrastructure supporting our AI solutions undergoes regular external audits to ensure compliance with security best practices.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-cyan-100 text-cyan-600">
                      <span className="text-2xl font-bold">🛡️</span>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Incident Response & Risk Management</h3>
                      <p className="mt-2 text-gray-600">Comprehensive protocols are in place for security threats, including rapid containment and mitigation strategies in case of breaches.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Control Section */}
          <div className="mt-16">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8">
                <h2 className="text-2xl font-bold text-gray-900">6. Full Control Over Your Data</h2>
                <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">AI-Generated Reports Are Yours</h3>
                      <p className="mt-2 text-gray-600">You retain full control over downloaded AI-generated reports and summaries.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Data Storage Responsibility</h3>
                      <p className="mt-2 text-gray-600">Firms requiring extended retention should store AI-generated outputs in their own compliant repositories.</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">On-Demand Data Deletion</h3>
                      <p className="mt-2 text-gray-600">Prestige AI can facilitate full deletion of data upon request, ensuring compliance with security and confidentiality expectations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA Section */}
          <div className="mt-16">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-8 sm:p-10">
                <h2 className="text-2xl font-bold text-white mb-4">
                  Confidently Leverage AI Without Compromising Security
                </h2>
                <p className="text-lg text-cyan-100 mb-8">
                  Prestige AI enables firms to harness AI-driven automation while maintaining full control over data security and confidentiality. Our secure AI solutions allow you to improve efficiency without compromising compliance, privacy, or professional standards.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-between">
                  <p className="text-cyan-100 mb-4 sm:mb-0">
                    For security inquiries or data management requests, please contact:
                  </p>
                  <a
                    href="mailto:support@prestigeai.tech"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-cyan-700 bg-white hover:bg-cyan-50"
                  >
                    support@prestigeai.tech
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialityPage;